export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface FileDisplayData {
  id: string;
  cycleNum: number;
  productName?: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  uploadTime: number;
  isOffline: boolean;
}

const SP_SITE_URL = "https://bectors.sharepoint.com/sites/PTMS_PRD";
const SP_LIBRARY_TITLE = "CodingDocuments";

async function getSPFieldInternalNames(accessToken: string): Promise<{
  qualityInternal: string;
  cycleInternal: string;
  productInternal: string;
  entityType: string;
}> {
  try {
    const fieldsUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/fields?$filter=Hidden eq false&$select=InternalName,Title`;
    const fieldsRes = await fetch(fieldsUrl, {
      method: "GET",
      headers: {
        Accept: "application/json;odata=verbose",
        Authorization: `Bearer ${accessToken}`,
      },
    });

    let qualityInternal = "QualityId";
    let cycleInternal = "CycleNum";
    let productInternal = "ProductName";

    if (fieldsRes.ok) {
      const fieldsData = await fieldsRes.json();
      const fields = fieldsData.d?.results || [];
      for (const field of fields) {
        const title = (field.Title || "").toLowerCase();
        const internalName = field.InternalName || "";
        if (title.includes("quality") || title.includes("qualityid")) qualityInternal = internalName;
        if (title.includes("cycle") && (title.includes("num") || title.includes("number"))) cycleInternal = internalName;
        if (title.includes("product") && title.includes("name")) productInternal = internalName;
      }
    }

    const entityRes = await fetch(
      `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')?$select=ListItemEntityTypeFullName`,
      {
        method: "GET",
        headers: {
          Accept: "application/json;odata=verbose",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    let entityType = "SP.Data.CodingDocumentsListItem";
    if (entityRes.ok) {
      const entityData = await entityRes.json();
      entityType = entityData.d?.ListItemEntityTypeFullName || entityType;
    }
    return { qualityInternal, cycleInternal, productInternal, entityType };
  } catch (error) {
    console.warn("Failed to discover CodingDocuments field names:", error);
    return {
      qualityInternal: "QualityId",
      cycleInternal: "CycleNum",
      productInternal: "ProductName",
      entityType: "SP.Data.CodingDocumentsListItem",
    };
  }
}

/**
 * Upload file to SharePoint CodingDocuments library.
 * @param fileInputId - ID of the file input element (e.g. backing-coding-attachment-1)
 */
export async function uploadFileToSharePoint(
  cycleNum: number,
  QualityTourId: string,
  accessToken: string,
  productName?: string,
  fileInputId?: string
): Promise<SaveResponse> {
  const inputId = fileInputId || `backing-coding-attachment-${cycleNum}`;
  try {
    if (!accessToken) throw new Error("Access token is invalid or missing");
    if (!cycleNum || cycleNum < 1) throw new Error("Invalid cycle number");

    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    const file = fileInput?.files?.[0];
    if (!file) return { success: false, message: "Please select a file before uploading" };

    const digestResponse = await fetch(`${SP_SITE_URL}/_api/contextinfo`, {
      method: "POST",
      headers: { Accept: "application/json;odata=verbose", Authorization: `Bearer ${accessToken}` },
    });
    if (!digestResponse.ok) throw new Error(`Failed to get context info: ${digestResponse.status}`);
    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    const fileExtension = file.name.split(".").pop() || "";
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toTimeString().split(" ")[0].replace(/:/g, "-");
    const uniqueFileName = `${fileNameWithoutExt}_${dateStr}_${timeStr}.${fileExtension}`;

    const folderServerRelativeUrl = "/sites/PTMS_PRD/CodingDocuments";
    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${folderServerRelativeUrl}')/Files/add(url='${encodeURIComponent(uniqueFileName)}',overwrite=true)?$expand=ListItemAllFields`;

    const uploadRes = await fetch(uploadUrl, {
      method: "POST",
      headers: {
        Accept: "application/json;odata=verbose",
        "X-RequestDigest": digest,
        Authorization: `Bearer ${accessToken}`,
      },
      body: file,
    });

    if (!uploadRes.ok) {
      const errorText = await uploadRes.text();
      throw new Error(`Upload failed: ${uploadRes.status} - ${errorText}`);
    }

    const uploadJson = await uploadRes.json();
    const itemId = uploadJson?.d?.ListItemAllFields?.Id || uploadJson?.d?.Id || uploadJson?.d?.ListItemAllFields?.ID;
    if (!itemId) throw new Error("Upload succeeded but ListItem Id not found.");

    const SP_FIELDS = await getSPFieldInternalNames(accessToken);
    const qid = (QualityTourId || "").replace(/[{}]/g, "");
    const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${itemId})`;
    const cycleNumValue = Number(cycleNum);
    const trimmedProductName = productName != null && productName !== "" ? productName.trim() : null;

    console.log('Setting CodingDocuments metadata:', {
      qualityId: qid,
      cycleNum: cycleNumValue,
      productName: trimmedProductName,
      fieldNames: SP_FIELDS
    });

    // Build update body with all required fields
    const updateBody: Record<string, unknown> = {
      __metadata: { type: SP_FIELDS.entityType },
      [SP_FIELDS.qualityInternal]: qid,
      [SP_FIELDS.cycleInternal]: String(cycleNumValue),
    };
    if (trimmedProductName) {
      updateBody[SP_FIELDS.productInternal] = trimmedProductName;
    }

    // Try to update all fields at once
    let updateRes = await fetch(itemUrl, {
      method: "POST",
      headers: {
        Accept: "application/json;odata=verbose",
        "Content-Type": "application/json;odata=verbose",
        "X-RequestDigest": digest,
        Authorization: `Bearer ${accessToken}`,
        "IF-MATCH": "*",
        "X-HTTP-Method": "MERGE",
      },
      body: JSON.stringify(updateBody),
    });

    // If update failed, try updating fields individually
    if (!updateRes.ok) {
      const errorText = await updateRes.text();
      console.warn("CodingDocuments full metadata update failed, trying individual fields:", errorText);
      
      // Try QualityId first (required)
      const qualityUpdate = {
        __metadata: { type: SP_FIELDS.entityType },
        [SP_FIELDS.qualityInternal]: qid,
      };
      updateRes = await fetch(itemUrl, {
        method: "POST",
        headers: {
          Accept: "application/json;odata=verbose",
          "Content-Type": "application/json;odata=verbose",
          "X-RequestDigest": digest,
          Authorization: `Bearer ${accessToken}`,
          "IF-MATCH": "*",
          "X-HTTP-Method": "MERGE",
        },
        body: JSON.stringify(qualityUpdate),
      });

      // Then CycleNum
      if (updateRes.ok) {
        const cycleUpdate = {
          __metadata: { type: SP_FIELDS.entityType },
          [SP_FIELDS.cycleInternal]: String(cycleNumValue),
        };
        updateRes = await fetch(itemUrl, {
          method: "POST",
          headers: {
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            Authorization: `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE",
          },
          body: JSON.stringify(cycleUpdate),
        });
      }

      // Finally ProductName if provided
      if (updateRes.ok && trimmedProductName) {
        const productUpdate = {
          __metadata: { type: SP_FIELDS.entityType },
          [SP_FIELDS.productInternal]: trimmedProductName,
        };
        updateRes = await fetch(itemUrl, {
          method: "POST",
          headers: {
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            Authorization: `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE",
          },
          body: JSON.stringify(productUpdate),
        });
      }

      if (!updateRes.ok) {
        const finalError = await updateRes.text();
        console.error("CodingDocuments metadata update failed after retries:", finalError);
        throw new Error(`Failed to set file metadata: ${finalError}`);
      } else {
        console.log('CodingDocuments metadata updated successfully (individual fields)');
      }
    } else {
      console.log('CodingDocuments metadata updated successfully (all fields at once)');
    }

    return {
      success: true,
      message: `File "${uniqueFileName}" uploaded successfully.`,
      data: { itemId, uploadJson, fileName: uniqueFileName },
    };
  } catch (err) {
    console.error("BackingCoding upload error:", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Failed to upload file.",
    };
  }
}

/**
 * Upload with File object (for offline sync).
 */
export async function uploadFileToSharePointWithFile(
  cycleNum: number,
  QualityTourId: string,
  accessToken: string,
  file: File,
  productName?: string
): Promise<SaveResponse> {
  try {
    if (!accessToken) throw new Error("Access token is invalid or missing");
    if (!file) return { success: false, message: "File is required" };
    if (!cycleNum || cycleNum < 1) throw new Error("Invalid cycle number");

    const digestResponse = await fetch(`${SP_SITE_URL}/_api/contextinfo`, {
      method: "POST",
      headers: { Accept: "application/json;odata=verbose", Authorization: `Bearer ${accessToken}` },
    });
    if (!digestResponse.ok) throw new Error(`Failed to get context info: ${digestResponse.status}`);
    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    const fileExtension = file.name.split(".").pop() || "";
    const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
    const now = new Date();
    const uniqueFileName = `${fileNameWithoutExt}_${now.toISOString().split("T")[0]}_${now.toTimeString().split(" ")[0].replace(/:/g, "-")}.${fileExtension}`;

    const folderServerRelativeUrl = "/sites/PTMS_PRD/CodingDocuments";
    const uploadUrl = `${SP_SITE_URL}/_api/web/GetFolderByServerRelativeUrl('${folderServerRelativeUrl}')/Files/add(url='${encodeURIComponent(uniqueFileName)}',overwrite=true)?$expand=ListItemAllFields`;

    const uploadRes = await fetch(uploadUrl, {
      method: "POST",
      headers: { Accept: "application/json;odata=verbose", "X-RequestDigest": digest, Authorization: `Bearer ${accessToken}` },
      body: file,
    });

    if (!uploadRes.ok) throw new Error(`Upload failed: ${uploadRes.status} - ${await uploadRes.text()}`);

    const uploadJson = await uploadRes.json();
    const itemId = uploadJson?.d?.ListItemAllFields?.Id || uploadJson?.d?.Id;
    if (!itemId) throw new Error("Upload succeeded but ListItem Id not found.");

    const SP_FIELDS = await getSPFieldInternalNames(accessToken);
    const qid = (QualityTourId || "").replace(/[{}]/g, "");
    const itemUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${itemId})`;
    const trimmedProductName = productName != null && productName !== "" ? productName.trim() : null;

    console.log('Setting CodingDocuments metadata (with file):', {
      qualityId: qid,
      cycleNum,
      productName: trimmedProductName,
      fieldNames: SP_FIELDS
    });

    const updateBody: Record<string, unknown> = {
      __metadata: { type: SP_FIELDS.entityType },
      [SP_FIELDS.qualityInternal]: qid,
      [SP_FIELDS.cycleInternal]: String(cycleNum),
    };
    if (trimmedProductName) {
      updateBody[SP_FIELDS.productInternal] = trimmedProductName;
    }

    let updateRes = await fetch(itemUrl, {
      method: "POST",
      headers: {
        Accept: "application/json;odata=verbose",
        "Content-Type": "application/json;odata=verbose",
        "X-RequestDigest": digest,
        Authorization: `Bearer ${accessToken}`,
        "IF-MATCH": "*",
        "X-HTTP-Method": "MERGE",
      },
      body: JSON.stringify(updateBody),
    });

    // If update failed, try individual fields
    if (!updateRes.ok) {
      const errorText = await updateRes.text();
      console.warn("CodingDocuments metadata update failed, trying individual fields:", errorText);
      
      // Update QualityId
      const qualityUpdate = { __metadata: { type: SP_FIELDS.entityType }, [SP_FIELDS.qualityInternal]: qid };
      updateRes = await fetch(itemUrl, {
        method: "POST",
        headers: {
          Accept: "application/json;odata=verbose",
          "Content-Type": "application/json;odata=verbose",
          "X-RequestDigest": digest,
          Authorization: `Bearer ${accessToken}`,
          "IF-MATCH": "*",
          "X-HTTP-Method": "MERGE",
        },
        body: JSON.stringify(qualityUpdate),
      });

      // Update CycleNum
      if (updateRes.ok) {
        const cycleUpdate = { __metadata: { type: SP_FIELDS.entityType }, [SP_FIELDS.cycleInternal]: String(cycleNum) };
        updateRes = await fetch(itemUrl, {
          method: "POST",
          headers: {
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            Authorization: `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE",
          },
          body: JSON.stringify(cycleUpdate),
        });
      }

      // Update ProductName
      if (updateRes.ok && trimmedProductName) {
        const productUpdate = { __metadata: { type: SP_FIELDS.entityType }, [SP_FIELDS.productInternal]: trimmedProductName };
        updateRes = await fetch(itemUrl, {
          method: "POST",
          headers: {
            Accept: "application/json;odata=verbose",
            "Content-Type": "application/json;odata=verbose",
            "X-RequestDigest": digest,
            Authorization: `Bearer ${accessToken}`,
            "IF-MATCH": "*",
            "X-HTTP-Method": "MERGE",
          },
          body: JSON.stringify(productUpdate),
        });
      }

      if (!updateRes.ok) {
        const finalError = await updateRes.text();
        console.error("CodingDocuments metadata update failed after retries:", finalError);
        throw new Error(`Failed to set file metadata: ${finalError}`);
      } else {
        console.log('CodingDocuments metadata updated successfully (individual fields)');
      }
    } else {
      console.log('CodingDocuments metadata updated successfully (all fields at once)');
    }

    return { success: true, message: `File "${uniqueFileName}" uploaded.`, data: { itemId, fileName: uniqueFileName } };
  } catch (error) {
    console.error("BackingCoding upload (with file) error:", error);
    return { success: false, message: error instanceof Error ? error.message : "Failed to upload file." };
  }
}

/**
 * Fetch uploaded files from CodingDocuments for a Quality Tour.
 */
export async function fetchUploadedFiles(QualityTourId: string, accessToken: string): Promise<FileDisplayData[]> {
  try {
    if (!accessToken) throw new Error("Access token is invalid or missing");
    const qid = (QualityTourId || "").replace(/[{}]/g, "");

    let cycleInternal = "CycleNum";
    let productInternal = "ProductName";
    try {
      const fieldNames = await getSPFieldInternalNames(accessToken);
      cycleInternal = fieldNames.cycleInternal;
      productInternal = fieldNames.productInternal;
    } catch (_) {}

    const fetchUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=Id,File/Name,File/ServerRelativeUrl,File/Length,Modified,${cycleInternal},${productInternal}&$expand=File`;
    const headers = { Accept: "application/json;odata=verbose", Authorization: `Bearer ${accessToken}` };
    let response = await fetch(fetchUrl, { method: "GET", headers });
    if (!response.ok) {
      response = await fetch(
        `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items?$filter=QualityId eq '${qid}'&$select=*&$expand=File`,
        { method: "GET", headers }
      );
    }
    if (!response.ok) return [];

    const data = await response.json();
    const items = data.d?.results || [];
    const spOrigin = new URL(SP_SITE_URL).origin;

    const getCycle = (item: any): number => {
      const v = item[cycleInternal] ?? item.CycleNum ?? item.cycleNum;
      if (v == null || v === "") return 1;
      const num = typeof v === "string" ? parseInt(v.replace(/\D/g, ""), 10) : Number(v);
      return isNaN(num) || num < 1 ? 1 : num;
    };
    const getProduct = (item: any): string | undefined => {
      const v = item[productInternal] ?? item.ProductName;
      if (v != null && String(v).trim() !== "") return String(v).trim();
      return undefined;
    };

    return items.map((item: any) => {
      const serverRelative = item.File?.ServerRelativeUrl;
      const fileUrl = serverRelative ? `${spOrigin}${serverRelative}?web=1` : "";
      return {
        id: String(item.Id),
        cycleNum: getCycle(item),
        productName: getProduct(item),
        fileUrl,
        fileName: item.File?.Name || "Unknown",
        fileSize: item.File?.Length || 0,
        uploadTime: new Date(item.Modified).getTime(),
        isOffline: false,
      };
    });
  } catch (err) {
    console.error("Error fetching CodingDocuments files:", err);
    return [];
  }
}

/**
 * Delete file from CodingDocuments.
 */
export async function deleteFileFromSharePoint(fileId: string, accessToken: string): Promise<SaveResponse> {
  try {
    if (!accessToken) throw new Error("Access token is invalid or missing");
    if (!fileId) throw new Error("File ID is required");

    const digestResponse = await fetch(`${SP_SITE_URL}/_api/contextinfo`, {
      method: "POST",
      headers: { Accept: "application/json;odata=verbose", Authorization: `Bearer ${accessToken}` },
    });
    if (!digestResponse.ok) throw new Error(`Failed to get context info: ${digestResponse.status}`);
    const digestData = await digestResponse.json();
    const digest = digestData.d.GetContextWebInformation.FormDigestValue;

    const deleteUrl = `${SP_SITE_URL}/_api/web/lists/getbytitle('${SP_LIBRARY_TITLE}')/items(${fileId})`;
    const deleteRes = await fetch(deleteUrl, {
      method: "POST",
      headers: {
        Accept: "application/json;odata=verbose",
        "X-RequestDigest": digest,
        Authorization: `Bearer ${accessToken}`,
        "IF-MATCH": "*",
        "X-HTTP-Method": "DELETE",
      },
    });

    if (!deleteRes.ok) throw new Error(`Delete failed: ${deleteRes.status} - ${await deleteRes.text()}`);
    return { success: true, message: "File deleted successfully" };
  } catch (err) {
    console.error("CodingDocuments delete error:", err);
    return { success: false, message: err instanceof Error ? err.message : "Failed to delete file." };
  }
}
